import { CoachingSectionCard }
  from "@/components/coaching/CoachingSectionCard";
import {
  describeMissingInformation,
  looksLikeInternalCode,
} from "@/lib/missingInformationCopy";

const ACTION_LABELS = {
  UPDATE_PROFILE: "Update profile",
  UPDATE_GOAL: "Set training goal",
  COMPLETE_GOAL_SETUP: "Set training goal",
  ADD_AVAILABILITY: "Set training goal",
  UPDATE_AVAILABILITY: "Set training goal",
  CONNECT_SENSOR: "Connect sensor",
  SYNC_ACTIVITY: "Sync activity",
  ADD_RPE: "Add RPE",
  REQUEST_REASSESSMENT: "Request reassessment",
  CONTACT_SUPPORT: "Contact support",
  NO_ACTION_AVAILABLE: null,
};

const GOAL_ACTION_HREF = {
  UPDATE_GOAL: "/coach#training-goal",
  COMPLETE_GOAL_SETUP: "/coach#training-goal",
  ADD_AVAILABILITY: "/coach#training-goal",
  UPDATE_AVAILABILITY: "/coach#training-goal",
};

const CRITICALITY_STYLE = {
  CRITICAL: "border-analytics-red text-analytics-red",
  HIGH: "border-analytics-amber text-analytics-amber",
  MODERATE: "border-analytics-blue text-analytics-blue",
  LOW: "border-dark-border text-dark-text-secondary",
};

const CRITICALITY_LABEL = {
  CRITICAL: "Critical", HIGH: "High", MODERATE: "Moderate", LOW: "Low",
};

const CATEGORY_LABEL = {
  PROFILE: "Profile", GOAL: "Goal", AVAILABILITY: "Availability",
  SENSOR: "Sensor", COMPLIANCE: "Compliance", READINESS: "Readiness",
  ACTIVITY: "Activity", RPE: "Perceived effort",
};

const SECTION_LABEL = {
  today: "Today", current_week: "This week",
  compliance: "Compliance", readiness: "Readiness",
  adaptation: "Plan changes", safety: "Safety",
};

const humanise = (raw, table) => {
  if (!raw) return null;
  const key = String(raw).toUpperCase();
  return table[key] || String(raw).toLowerCase().replaceAll("_", " ");
};

/**
 * Dedupe missing-information items by category+field, preserving the
 * first (most critical) occurrence.
 */
const dedupe = (items = []) => {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const key = `${item.category || ""}::${item.field || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

export const MissingInformationCard = ({ items, error }) => {
  const deduped = dedupe(items || []);
  const isEmpty = !error && deduped.length === 0;
  return (
    <CoachingSectionCard
      title="Missing information"
      headingId="coaching-missing-heading"
      testId="coaching-missing-card"
      error={error}
      empty={isEmpty ? "Nothing critical is missing." : null}
    >
      {!isEmpty && (
        <ul className="space-y-2" data-testid="missing-info-list">
          {deduped.map((item, idx) => {
            const critStyle = CRITICALITY_STYLE[item.criticality]
                                    || CRITICALITY_STYLE.MODERATE;
            const mapped = describeMissingInformation(
              item.field || item.code,
              item.description,
            );
            const nextAction = item.next_action || item.suggested_next_action;
            const actionHref = mapped.href || GOAL_ACTION_HREF[nextAction] || null;
            const actionLabel = mapped.actionLabel
              || (nextAction ? ACTION_LABELS[nextAction] : null);
            const rawDescription = item.description || item.field
                                       || humanise(item.category, CATEGORY_LABEL)
                                       || "Additional information needed";
            const description = looksLikeInternalCode(rawDescription)
              ? mapped.message
              : rawDescription;
            const category = humanise(item.category, CATEGORY_LABEL)
                                       || "Info";
            const criticality = CRITICALITY_LABEL[item.criticality]
                                       || "Low priority";
            const affected = humanise(item.affected_section, SECTION_LABEL);
            return (
              <li key={`${item.category}-${item.field || item.description}-${idx}`}
                    className={`app-card-elevated p-3 flex items-start justify-between gap-3 border-l-[3px] ${critStyle}`}>
                <div className="min-w-0">
                  <div className="card-label"
                            aria-label={`${category}, ${criticality} priority`}>
                    {category} · {criticality}
                  </div>
                  <div className="text-sm text-dark-text-primary break-words">
                    {description}
                  </div>
                  {(affected || item.blocks_coaching
                          || item.lowers_confidence) && (
                    <div className="text-[11px] text-dark-text-muted mt-1">
                      {affected && <>Affects: {affected}</>}
                      {item.blocks_coaching && " · blocks coaching"}
                      {item.lowers_confidence && " · lowers confidence"}
                    </div>
                  )}
                </div>
                {actionLabel && actionHref && (
                  <a
                    href={actionHref}
                    className="shrink-0 text-[11px] uppercase tracking-widest border border-current px-2 py-1 rounded"
                    data-testid={`missing-action-${nextAction || "SET_GOAL"}`}
                  >
                    {actionLabel}
                  </a>
                )}
                {actionLabel && !actionHref && (
                  <span className="shrink-0 text-[11px] uppercase tracking-widest border border-current px-2 py-1 rounded"
                          data-testid={`missing-action-${nextAction}`}>
                    {actionLabel}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </CoachingSectionCard>
  );
};
